import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Zap,
  Upload,
  FileText,
  Plus,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  Rocket,
  Globe,
} from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate, cn } from '@/lib/utils'
import toast from 'react-hot-toast'

type QueueItem = {
  id: string
  domainName: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  resultJson?: any
  createdAt: number
}

export function BatchPage() {
  const [domains, setDomains] = useState<string[]>([])
  const [newDomain, setNewDomain] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['batch-status'],
    queryFn: () => api.getBatchStatus(),
    refetchInterval: 5000, // Poll every 5 seconds
  })

  const queueMutation = useMutation({
    mutationFn: (domainList: string[]) => api.queueBatch(domainList),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-status'] })
      setDomains([])
      toast.success(`${domains.length} domains queued for processing!`)
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.retryBatchItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch-status'] })
      toast.success('Retrying...')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const handleAddDomain = () => {
    if (newDomain.trim() && !domains.includes(newDomain.trim())) {
      setDomains([...domains, newDomain.trim()])
      setNewDomain('')
    }
  }

  const handleRemoveDomain = (index: number) => {
    setDomains(domains.filter((_, i) => i !== index))
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const lines = text.split('\n').map((line) => line.trim()).filter(Boolean)
      const newDomains = lines.filter((d) => !domains.includes(d))
      setDomains([...domains, ...newDomains])
      toast.success(`Added ${newDomains.length} domains from CSV`)
    }
    reader.readAsText(file)

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleStartBatch = () => {
    if (domains.length === 0) {
      toast.error('Add at least one domain')
      return
    }
    queueMutation.mutate(domains)
  }

  const queueItems: QueueItem[] = data?.queue || []
  const pendingCount = queueItems.filter((q) => q.status === 'pending').length
  const processingCount = queueItems.filter((q) => q.status === 'processing').length
  const completedCount = queueItems.filter((q) => q.status === 'completed').length
  const failedCount = queueItems.filter((q) => q.status === 'failed').length

  const getStatusIcon = (status: QueueItem['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-muted-foreground" />
      case 'processing':
        return <Loader2 className="h-4 w-4 text-primary animate-spin" />
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-success" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />
    }
  }

  const getStatusColor = (status: QueueItem['status']) => {
    switch (status) {
      case 'pending':
        return 'badge-muted'
      case 'processing':
        return 'badge-primary'
      case 'completed':
        return 'badge-success'
      case 'failed':
        return 'badge-destructive'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl bg-gradient-to-br from-accent/20 to-primary/20">
          <Zap className="h-8 w-8 text-accent" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-bold">YOLO Mode</h1>
          <p className="text-muted-foreground">
            Batch process multiple domains autonomously
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="card bg-muted/50">
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-2xl font-display font-bold">{pendingCount}</p>
              <p className="text-sm text-muted-foreground">Pending</p>
            </div>
          </div>
        </div>
        <div className="card bg-primary/5 border-primary/20">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
            <div>
              <p className="text-2xl font-display font-bold text-primary">{processingCount}</p>
              <p className="text-sm text-muted-foreground">Processing</p>
            </div>
          </div>
        </div>
        <div className="card bg-success/5 border-success/20">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-success" />
            <div>
              <p className="text-2xl font-display font-bold text-success">{completedCount}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </div>
        </div>
        <div className="card bg-destructive/5 border-destructive/20">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-2xl font-display font-bold text-destructive">{failedCount}</p>
              <p className="text-sm text-muted-foreground">Failed</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Add Domains */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Add Domains</h2>

          <div className="space-y-4">
            {/* Manual Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                placeholder="example.com"
                className="input flex-1"
              />
              <button onClick={handleAddDomain} className="btn-primary">
                <Plus className="h-4 w-4" />
              </button>
            </div>

            {/* CSV Upload */}
            <div className="relative">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".csv,.txt"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full btn-outline justify-center"
              >
                <Upload className="h-4 w-4" />
                Upload CSV
              </button>
            </div>

            {/* Domain List */}
            {domains.length > 0 && (
              <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-auto">
                {domains.map((domain, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{domain}</span>
                    </div>
                    <button
                      onClick={() => handleRemoveDomain(i)}
                      className="btn-ghost p-1 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {domains.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Add domains manually or upload a CSV file</p>
              </div>
            )}

            {/* Start Button */}
            <button
              onClick={handleStartBatch}
              disabled={domains.length === 0 || queueMutation.isPending}
              className="w-full btn-primary py-3 justify-center"
            >
              {queueMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Queueing...
                </>
              ) : (
                <>
                  <Rocket className="h-5 w-5" />
                  Start Processing ({domains.length} domains)
                </>
              )}
            </button>
          </div>
        </div>

        {/* Processing Queue */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Processing Queue</h2>
            <button
              onClick={() => refetch()}
              className="btn-ghost btn-sm"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : queueItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Zap className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No items in queue</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-auto">
              <AnimatePresence>
                {queueItems.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2, delay: i * 0.02 }}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg border transition-colors',
                      item.status === 'processing'
                        ? 'border-primary/50 bg-primary/5'
                        : item.status === 'completed'
                        ? 'border-success/50 bg-success/5'
                        : item.status === 'failed'
                        ? 'border-destructive/50 bg-destructive/5'
                        : 'border-border'
                    )}
                  >
                    {getStatusIcon(item.status)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.domainName}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(item.createdAt)}
                      </p>
                    </div>
                    <span className={cn('badge', getStatusColor(item.status))}>
                      {item.status}
                    </span>
                    {item.status === 'failed' && (
                      <button
                        onClick={() => retryMutation.mutate(item.id)}
                        disabled={retryMutation.isPending}
                        className="btn-ghost btn-sm"
                        title="Retry"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      {/* How It Works */}
      <div className="card bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5">
        <h2 className="text-lg font-semibold mb-4">How YOLO Mode Works</h2>
        <div className="grid gap-6 sm:grid-cols-3">
          <div className="flex gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold">
              1
            </div>
            <div>
              <p className="font-medium mb-1">Add Domains</p>
              <p className="text-sm text-muted-foreground">
                Enter domains manually or upload a CSV file with one domain per line
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground font-bold">
              2
            </div>
            <div>
              <p className="font-medium mb-1">AI Processes</p>
              <p className="text-sm text-muted-foreground">
                Each domain is analyzed, content generated, and site created automatically
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold">
              3
            </div>
            <div>
              <p className="font-medium mb-1">Deploy & Monitor</p>
              <p className="text-sm text-muted-foreground">
                Sites deploy to Cloudflare automatically. Track results in the Sites page
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-4 p-4 rounded-lg bg-accent/10 border border-accent/20">
        <AlertCircle className="h-5 w-5 text-accent shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-accent">Pro Feature</p>
          <p className="text-sm text-muted-foreground">
            YOLO mode is available on the Pro plan. Each domain uses AI credits from your plan.
            Processing large batches may take several minutes.
          </p>
        </div>
      </div>
    </div>
  )
}
